import { pluralize } from '@Helpers/util';
import {
  COMPANY_SETTINGS_LOADED,
  COMPANY_SETTINGS_CLEAR,
  COMPANY_SINGLE_SETTING_LOADED,
  COMPANY_SINGLE_SETTING_CLEAR,
  COMPANY_NOTIFICATION_SETTINGS_LOADED,
  COMPANY_NOTIFICATION_SETTINGS_CLEAR,
  COMPANY_MAINTENANCE_SETTINGS_LOADED,
  COMPANY_MAINTENANCE_SETTINGS_CLEAR,
  COMPANY_UNITS_LOADED,
  COMPANY_UNITS_CLEAR,
} from '../actionTypes';

import * as api from '../apiHelper';
import { initiateRedirectRequest } from '../redirectActions';
import {
  setFetchLoaderLoading,
  setFetchLoaderReset,
  setSubmissionLoaderReset,
  setSubmissionLoaderLoading,
} from '../fetchLoaderActions';
import { setFaviconLoading, setFaviconDefault } from '../faviconActions';
import { arriveNotice } from '../noticeActions';

// Reducer corresponding actions
function companySettingsLoaded(settings) {
  return {
    type: COMPANY_SETTINGS_LOADED,
    payload: { settings },
  };
}

function companySettingsClear() {
  return { type: COMPANY_SETTINGS_CLEAR };
}

function companySingleSettingLoaded(setting) {
  return {
    type: COMPANY_SINGLE_SETTING_LOADED,
    payload: { setting },
  };
}

function companySingleSettingClear() {
  return { type: COMPANY_SINGLE_SETTING_CLEAR };
}

function companyNotificationSettingLoaded(settings) {
  return {
    type: COMPANY_NOTIFICATION_SETTINGS_LOADED,
    payload: { settings },
  };
}

function companyNotificationSettingClear() {
  return { type: COMPANY_NOTIFICATION_SETTINGS_CLEAR };
}

function companyMaintenanceSettingsLoaded(settings) {
  return {
    type: COMPANY_MAINTENANCE_SETTINGS_LOADED,
    payload: { settings },
  };
}

function companyMaintenanceSettingsClear() {
  return { type: COMPANY_MAINTENANCE_SETTINGS_CLEAR };
}

function companyUnitsLoaded(units) {
  return {
    type: COMPANY_UNITS_LOADED,
    payload: { units },
  };
}

function companyUnitsClear() {
  return { type: COMPANY_UNITS_CLEAR };
}

// API
async function getCompanySettings(args, token) {
  const response = await api.get({
    path: 'settings',
    params: args,
    token,
    version: 'v3',
  });
  return response.json();
}

async function getSingleCompanySetting(name, args, token) {
  const response = await api.get({
    path: `settings/${name}`,
    params: args,
    token,
    version: 'v3',
  });
  return response.json();
}

async function updateCompanyDomainApi(name, body, token) {
  const response = await api.put({
    path: `settings/${name}`,
    body,
    token,
    version: 'v3',
  });
  return response.json();
}

async function getCompanyNotificationSetting(args, token) {
  const response = await api.get({
    path: 'settings/notification',
    params: args,
    token,
    version: 'v3',
  });
  return response.json();
}

async function updateCompanyNotificationSetting(body, token) {
  const response = await api.put({
    path: 'settings/notification',
    body,
    token,
    version: 'v3',
  });
  return response.json();
}

async function getCompanyMaintenanceSettings(args, token) {
  const response = await api.get({
    path: 'maintenance_settings',
    params: args,
    token,
    version: 'v4',
  });
  return response.json();
}

async function getCompanyUnits(token) {
  const response = await api.get({
    path: 'settings/company_units',
    token,
    version: 'v3',
  });
  return response.json();
}

async function addCompanyUnit(body, token) {
  const response = await api.post({
    path: 'settings/add_unit',
    body,
    token,
    version: 'v3',
  });
  return response.json();
}

async function updateCompanyUnits(body, token) {
  const response = await api.post({
    path: 'settings/edit_units',
    body,
    token,
    version: 'v3',
  });
  return response.json();
}

async function deleteCompanyUnit(body, token) {
  const response = await api.post({
    path: 'settings/delete_unit',
    body,
    token,
    version: 'v3',
  });
  return response.json();
}

// Helper
async function fetchCompanySettings(dispatch, args, token) {
  try {
    const response = await getCompanySettings(args, token);

    dispatch(setFaviconDefault());
    dispatch(companySettingsLoaded(response));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      dispatch(arriveNotice(e.message));
    }
  }
}

async function fetchSingleCompanySetting(dispatch, name, args, token) {
  try {
    const response = await getSingleCompanySetting(name, args, token);

    dispatch(setFaviconDefault());
    dispatch(companySingleSettingLoaded(response));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      dispatch(arriveNotice(e.message));
    }
  }
}

async function fetchCompanyNotificationSetting(dispatch, args, token) {
  try {
    const response = await getCompanyNotificationSetting(args, token);

    dispatch(setFaviconDefault());
    dispatch(companyNotificationSettingLoaded(response));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      dispatch(arriveNotice(e.message));
    }
  }
}

async function updateCompanyDomain(
  dispatch,
  name,
  body,
  token,
  redirectPath,
  afterAction,
  successNotice,
) {
  try {
    await updateCompanyDomainApi(name, body, token);

    dispatch(arriveNotice(successNotice, 'ok'));
    dispatch(setSubmissionLoaderReset());
    if (redirectPath) dispatch(initiateRedirectRequest(null, redirectPath, true));
    afterAction();
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setSubmissionLoaderReset());
      dispatch(arriveNotice(e.message));
    }
  }
}

async function updateCompanyNotificationSettingApi(
  dispatch,
  body,
  token,
  afterAction,
) {
  try {
    await updateCompanyNotificationSetting(body, token);

    dispatch(arriveNotice('Notification setting updated!', 'ok'));
    afterAction();
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  }
}

async function fetchCompanyMaintenanceSettings(dispatch, args, token) {
  try {
    const response = await getCompanyMaintenanceSettings(args, token);

    dispatch(companyMaintenanceSettingsLoaded(response));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  }
}

// Public components actions
export function clearCompanySettingsRequest() {
  return (dispatch) => {
    dispatch(companySettingsClear());
  };
}

export function initiateCompanySettingsRequest(args) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      favicon: { faviconStatus },
    } = getState();

    // Don't use global loading state to prevent page re-renders
    // Only set favicon loading if not already loading
    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchCompanySettings(dispatch, args, token);
  };
}

export function clearSingleCompanySettingRequest() {
  return (dispatch) => {
    dispatch(companySingleSettingClear());
  };
}

export function initiateSingleCompanySettingRequest(name, args) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      favicon: { faviconStatus },
    } = getState();

    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchSingleCompanySetting(dispatch, name, args, token);
  };
}

export function updateCompanyDomainRequest(
  name,
  body,
  redirectPath = null,
  afterAction,
  successNotice,
) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      fetchLoader: { submissionLoading },
    } = getState();

    if (!submissionLoading) dispatch(setSubmissionLoaderLoading());

    await updateCompanyDomain(
      dispatch,
      name,
      body,
      token,
      redirectPath,
      afterAction,
      successNotice,
    );
  };
}

export function clearCompanyNotificationSettingRequest() {
  return (dispatch) => {
    dispatch(companyNotificationSettingClear());
  };
}

export function initiateCompanyNotificationSettingRequest(args) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      favicon: { faviconStatus },
    } = getState();

    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchCompanyNotificationSetting(dispatch, args, token);
  };
}

export function updateCompanyNotificationSettingRequest(body, afterAction) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
    } = getState();

    await updateCompanyNotificationSettingApi(
      dispatch,
      body,
      token,
      afterAction,
    );
  };
}

export function clearCompanyMaintenanceSettingsRequest() {
  return (dispatch) => {
    dispatch(companyMaintenanceSettingsClear());
  };
}

export function initiateCompanyMaintenanceSettingsRequest(args) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
    } = getState();

    await fetchCompanyMaintenanceSettings(dispatch, args, token);
  };
}

export function clearCompanyUnitsRequest() {
  return (dispatch) => {
    dispatch(companyUnitsClear());
  };
}

export function initiateCompanyUnitsRequest() {
  return async (dispatch, getState) => {
    try {
      dispatch(companyUnitsClear());
      const { auth: { token }, fetchLoader: { dataLoading } } = getState();
      if (!dataLoading) dispatch(setFetchLoaderLoading());

      const response = await getCompanyUnits(token);
      dispatch(companyUnitsLoaded(response));
      dispatch(setFetchLoaderReset());
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(arriveNotice(e.message));
        dispatch(setFetchLoaderReset());
      }
    }
  };
}

export function updateCompanyUnitsRequest(unitTerm, body, actionType) {
  return async (dispatch, getState) => {
    try {
      const { auth: { token }, fetchLoader: { submissionLoading } } = getState();
      if (!submissionLoading) dispatch(setSubmissionLoaderLoading());
      const unitTermPlural = pluralize(unitTerm);

      let response;
      switch (actionType) {
        case 'push':
          response = await addCompanyUnit(body, token);
          break;
        case 'delete':
          response = await deleteCompanyUnit(body, token);
          break;
        case 'edit':
          response = await updateCompanyUnits(body, token);
          break;
        default:
          throw new Error(`Invalid action type for updating ${unitTermPlural}`);
      }

      dispatch(companyUnitsLoaded(response));
      dispatch(arriveNotice(`${unitTermPlural} updated!`, 'ok'));
      dispatch(setSubmissionLoaderReset());
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(arriveNotice(e.message));
        dispatch(setSubmissionLoaderReset());
      }
    }
  };
}
